/**
 * R2 Measurement Overlay — Global precision measurement instrument.
 * Activates over any page content. Hybrid CAD-like navigation:
 *   Left-click      = place point
 *   Right-click     = cancel last point
 *   ESC             = exit measure mode
 *   Middle-drag     = pan
 *   Wheel           = browser zoom (pass-through)
 *   Spacebar (hold) = temporary pan hand tool
 *   Double-click    = complete measurement
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Ruler, Crosshair, Pentagon, Circle, CornerUpRight, Minus,
  Triangle, Zap, X, Undo2, Redo2, Layers, RotateCcw,
  Target, Move, ChevronDown,
} from 'lucide-react';
import {
  useR2MeasurementStore,
  type MeasurementPoint, type ActiveTool, type MeasurementType,
  type Measurement, type SnapPoint,
  LAYER_COLORS, BUILT_IN_LAYERS,
  pointDist, polylineLength, polygonArea, angleBetween,
  makeId, formatValue, defaultMeasurementStyle,
} from '../../lib/r2-measurement/r2-measurement.store.js';

// ── Tool definitions ──────────────────────────────────────────────────────────
const TOOLS: { id: ActiveTool; label: string; icon: React.FC<any>; shortcut: string; minPts: number; description: string }[] = [
  { id: 'distance',  label: 'Distance',    icon: Minus,       shortcut: 'D', minPts: 2, description: 'Click 2 points' },
  { id: 'polyline',  label: 'Polyline',    icon: CornerUpRight,shortcut:'P', minPts: 2, description: 'Click points, double-click to finish' },
  { id: 'area',      label: 'Area',        icon: Pentagon,    shortcut: 'A', minPts: 3, description: 'Click vertices, double-click to close' },
  { id: 'angle',     label: 'Angle',       icon: Triangle,    shortcut: 'N', minPts: 3, description: 'Click: vertex, point A, point B' },
  { id: 'radius',    label: 'Radius',      icon: Circle,      shortcut: 'R', minPts: 2, description: 'Click: center, then edge' },
  { id: 'calibrate', label: 'Calibrate Scale', icon: Zap, shortcut: 'C', minPts: 2, description: 'Click 2 known points, enter real distance' },
];

const SNAP_GRID = 10; // pixels

// ── Snap engine ───────────────────────────────────────────────────────────────
function findSnap(mouse: MeasurementPoint, existingPts: MeasurementPoint[], gridOn: boolean): SnapPoint {
  const SNAP_THRESHOLD = 12;

  // Snap to existing measurement endpoints
  for (const pt of existingPts) {
    if (pointDist(mouse, pt) < SNAP_THRESHOLD) return { point: pt, type: 'endpoint' };
  }

  // Grid snap
  if (gridOn) {
    const gx = Math.round(mouse.x / SNAP_GRID) * SNAP_GRID;
    const gy = Math.round(mouse.y / SNAP_GRID) * SNAP_GRID;
    if (pointDist(mouse, { x: gx, y: gy }) < SNAP_THRESHOLD / 2) {
      return { point: { x: gx, y: gy }, type: 'grid' };
    }
  }

  return { point: mouse, type: 'free' };
}

// ── Format live measurement ───────────────────────────────────────────────────
function computeLive(tool: ActiveTool, pendingPts: MeasurementPoint[], cursor: MeasurementPoint, cal: ReturnType<typeof useR2MeasurementStore.getState>['calibrations'][string] | undefined) {
  const pts = [...pendingPts, cursor];
  let rawPx = 0;

  if (tool === 'distance' || tool === 'calibrate' || tool === 'radius') {
    if (pts.length >= 2) rawPx = pointDist(pts[0]!, pts[pts.length - 1]!);
  } else if (tool === 'polyline') {
    if (pts.length >= 2) rawPx = polylineLength(pts);
  } else if (tool === 'area') {
    if (pts.length >= 3) rawPx = polygonArea(pts);
  } else if (tool === 'angle') {
    if (pts.length === 3) {
      rawPx = angleBetween(pts[0]!, pts[1]!, pts[2]!);
      return `${rawPx.toFixed(2)}°`;
    }
  }

  if (!cal) return `${rawPx.toFixed(1)} px`;
  const real = rawPx / cal.pxPerUnit;
  return formatValue(real, cal.unit);
}

// ── SVG arrowhead marker ids ──────────────────────────────────────────────────
const ARROWHEAD_ID = 'r2-measure-arrow';

// ── Main component ────────────────────────────────────────────────────────────
export function R2MeasurementOverlay() {
  const store = useR2MeasurementStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgRef    = useRef<SVGSVGElement>(null);
  const [cursor, setCursor] = useState<MeasurementPoint>({ x: 0, y: 0 });
  const [gridSnap, setGridSnap] = useState(true);
  const [showCalDialog, setShowCalDialog] = useState(false);
  const [calDist, setCalDist] = useState('');
  const [calUnit, setCalUnit] = useState<'mm'|'m'|'ft'|'in'>('mm');
  const [calPts, setCalPts] = useState<MeasurementPoint[]>([]);
  const [isPanning, setIsPanning] = useState(false);
  const spaceHeld = useRef(false);
  const midMouseDown = useRef(false);
  const lastMidPos = useRef({ x: 0, y: 0 });
  const [showLayers, setShowLayers] = useState(false);

  const {
    overlayActive, activeTool, pendingPoints, activeLayer, currentPage,
    calibrations, measurements, activeUnit,
    setActiveTool, addPendingPoint, setPendingPoints, addMeasurement,
    undo, redo, setOverlayActive, setCurrentPage,
  } = store;

  const cal = calibrations[currentPage];

  // Track current route
  useEffect(() => {
    const update = () => setCurrentPage(window.location.hash.replace('#', '') || 'home');
    update();
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, [setCurrentPage]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!overlayActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOverlayActive(false); return; }
      if (e.key === ' ') { spaceHeld.current = true; return; }
      if (e.ctrlKey && e.key === 'z') { undo(); return; }
      if (e.ctrlKey && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) { redo(); return; }
      if (e.ctrlKey && e.shiftKey && e.key === 'V') {
        try { navigator.clipboard.readText().then((t) => console.log('Clipboard:', t)); } catch {}
      }
      const tool = TOOLS.find((t) => t.shortcut === e.key.toUpperCase());
      if (tool) setActiveTool(tool.id);
    };
    const onKeyUp = (e: KeyboardEvent) => { if (e.key === ' ') spaceHeld.current = false; };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKeyUp); };
  }, [overlayActive, undo, redo, setActiveTool, setOverlayActive]);

  // Global M/D shortcuts (activate overlay)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'm' || e.key === 'M') {
        setOverlayActive(true); setActiveTool('distance');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOverlayActive, setActiveTool]);

  // Mouse events
  const onMouseMove = useCallback((e: React.MouseEvent) => {
    const pt = { x: e.clientX, y: e.clientY };
    const all = measurements.filter((m) => m.page === currentPage).flatMap((m) => m.points);
    const snap = findSnap(pt, [...all, ...pendingPoints], gridSnap);
    store.setSnapPoint(snap);
    setCursor(snap.point);

    // Mid-mouse pan
    if (midMouseDown.current) {
      const dx = e.clientX - lastMidPos.current.x;
      const dy = e.clientY - lastMidPos.current.y;
      window.scrollBy(-dx, -dy);
      lastMidPos.current = { x: e.clientX, y: e.clientY };
    }
  }, [measurements, currentPage, pendingPoints, gridSnap, store]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1 || spaceHeld.current) {
      e.preventDefault();
      midMouseDown.current = true;
      lastMidPos.current = { x: e.clientX, y: e.clientY };
      setIsPanning(true);
      return;
    }
    if (e.button === 2) {
      // Cancel last point
      if (pendingPoints.length > 0) {
        setPendingPoints(pendingPoints.slice(0, -1));
      }
      return;
    }
    if (e.button !== 0 || !activeTool) return;

    const snap = findSnap({ x: e.clientX, y: e.clientY }, [], gridSnap);
    const pt = snap.point;

    if (activeTool === 'calibrate') {
      const next = [...calPts, pt];
      setCalPts(next);
      if (next.length === 2) {
        setShowCalDialog(true);
      }
      return;
    }

    addPendingPoint(pt);

    // Auto-complete single-click tools
    if (activeTool === 'distance' && pendingPoints.length === 1) {
      completeMeasurement([...pendingPoints, pt]);
    } else if (activeTool === 'radius' && pendingPoints.length === 1) {
      completeMeasurement([...pendingPoints, pt]);
    }
  }, [activeTool, pendingPoints, addPendingPoint, setPendingPoints, gridSnap, calPts]);

  const onMouseUp = useCallback((e: React.MouseEvent) => {
    if (e.button === 1 || spaceHeld.current) {
      midMouseDown.current = false;
      setIsPanning(false);
    }
  }, []);

  const onDoubleClick = useCallback((e: React.MouseEvent) => {
    if (!activeTool || pendingPoints.length < 2) return;
    if (activeTool === 'polyline' || activeTool === 'area' || activeTool === 'angle') {
      completeMeasurement(pendingPoints);
    }
  }, [activeTool, pendingPoints]);

  const completeMeasurement = useCallback((pts: MeasurementPoint[]) => {
    if (!activeTool || pts.length < 2) return;

    let rawPx = 0;
    let typeStr = activeTool as MeasurementType;

    if (activeTool === 'distance') rawPx = pointDist(pts[0]!, pts[1]!);
    else if (activeTool === 'polyline') rawPx = polylineLength(pts);
    else if (activeTool === 'area') rawPx = polygonArea(pts);
    else if (activeTool === 'radius') rawPx = pointDist(pts[0]!, pts[1]!);
    else if (activeTool === 'angle' && pts.length >= 3) rawPx = angleBetween(pts[0]!, pts[1]!, pts[2]!);

    const calResult = cal ? { value: rawPx / cal.pxPerUnit, unit: cal.unit } : null;
    const unit = calResult?.unit ?? 'px';
    const display = calResult ? formatValue(calResult.value, unit) : `${rawPx.toFixed(1)} px`;

    const m: Measurement = {
      id: makeId(),
      type: typeStr,
      points: pts,
      value: rawPx,
      calibratedValue: calResult?.value ?? null,
      unit,
      label: display,
      layer: activeLayer,
      collection: store.activeCollection ?? '',
      page: currentPage,
      pageTitle: document.title,
      style: defaultMeasurementStyle(activeLayer),
      notes: '',
      timestamp: new Date().toISOString(),
      calibrated: !!cal,
    };

    addMeasurement(m);
    setPendingPoints([]);
  }, [activeTool, cal, activeLayer, currentPage, store.activeCollection, addMeasurement, setPendingPoints]);

  const applyCalibration = () => {
    if (calPts.length < 2 || !calDist) return;
    const px = pointDist(calPts[0]!, calPts[1]!);
    const real = parseFloat(calDist);
    if (!real) return;
    store.setCalibration(currentPage, {
      page: currentPage,
      p1: calPts[0]!, p2: calPts[1]!,
      pixelDist: px, realDist: real,
      unit: calUnit,
      pxPerUnit: px / real,
      label: `${real} ${calUnit}`,
    });
    setShowCalDialog(false);
    setCalPts([]);
    setCalDist('');
    setActiveTool('distance');
  };

  if (!overlayActive) return null;

  const pageMeasurements = measurements.filter((m) => m.page === currentPage);
  const liveValue = activeTool && pendingPoints.length > 0 ? computeLive(activeTool, pendingPoints, cursor, cal) : null;
  const cursorStyle = isPanning ? 'grabbing' : activeTool ? 'crosshair' : 'default';

  // Build SVG path for pending polyline/area preview
  const pendingPath = pendingPoints.length > 0
    ? `M ${pendingPoints.map((p) => `${p.x} ${p.y}`).join(' L ')} L ${cursor.x} ${cursor.y}`
    : '';

  const layerColor = LAYER_COLORS[activeLayer] ?? '#5eead4';

  return createPortal(
    <>
      {/* ── SVG Annotation Layer ──────────────────────────────────────────── */}
      <svg
        ref={svgRef}
        style={{
          position: 'fixed', inset: 0, width: '100vw', height: '100vh',
          pointerEvents: 'none', zIndex: 9998, overflow: 'visible',
        }}
      >
        <defs>
          <marker id={`${ARROWHEAD_ID}-teal`} viewBox="0 0 10 10" refX="10" refY="5"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#5eead4" />
          </marker>
          {Object.entries(LAYER_COLORS).map(([layer, color]) => (
            <marker key={layer} id={`arrow-${layer}`} viewBox="0 0 10 10" refX="10" refY="5"
              markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
            </marker>
          ))}
        </defs>

        {/* Saved measurements for this page */}
        {pageMeasurements.map((m) => (
          <MeasurementSVG key={m.id} m={m} hovered={store.hoveredId === m.id}
            onHover={(id) => store.setHovered(id)} onDelete={() => store.deleteMeasurement(m.id)} />
        ))}

        {/* Live preview pending path */}
        {pendingPath && (
          <path d={pendingPath} fill={activeTool === 'area' ? `${layerColor}22` : 'none'}
            stroke={layerColor} strokeWidth={1.5} strokeDasharray="6 3" opacity={0.85} />
        )}

        {/* Pending points */}
        {pendingPoints.map((pt, i) => (
          <g key={i}>
            <circle cx={pt.x} cy={pt.y} r={4} fill="none" stroke={layerColor} strokeWidth={1.5} />
            <circle cx={pt.x} cy={pt.y} r={1.5} fill={layerColor} />
          </g>
        ))}

        {/* Calibration points */}
        {calPts.map((pt, i) => (
          <g key={`cal-${i}`}>
            <circle cx={pt.x} cy={pt.y} r={6} fill="none" stroke="#fbbf24" strokeWidth={1.5} />
            <line x1={pt.x - 8} y1={pt.y} x2={pt.x + 8} y2={pt.y} stroke="#fbbf24" strokeWidth={1} />
            <line x1={pt.x} y1={pt.y - 8} x2={pt.x} y2={pt.y + 8} stroke="#fbbf24" strokeWidth={1} />
          </g>
        ))}
        {calPts.length === 2 && (
          <line x1={calPts[0]?.x??0} y1={calPts[0]?.y??0} x2={calPts[1]?.x??0} y2={calPts[1]?.y??0}
            stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="6 3" />
        )}
      </svg>

      {/* ── Crosshair Canvas ──────────────────────────────────────────────── */}
      <CrosshairCanvas cursor={cursor} color={layerColor} snapType={store.snapPoint?.type ?? 'free'} />

      {/* ── Hit area (captures mouse events) ─────────────────────────────── */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          cursor: cursorStyle, userSelect: 'none',
        }}
        onMouseMove={onMouseMove}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onDoubleClick={onDoubleClick}
        onContextMenu={(e) => e.preventDefault()}
        onWheel={(e) => {
          // Pass-through wheel for page zoom/scroll
          e.stopPropagation();
        }}
      />

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      <div style={{
        position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)',
        zIndex: 10001, display: 'flex', alignItems: 'center', gap: 4,
        background: 'rgba(6,10,20,0.96)', border: '1px solid rgba(94,234,212,0.3)',
        borderRadius: 12, padding: '6px 10px', backdropFilter: 'blur(16px)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
      }}>
        {/* Layer selector */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowLayers((v) => !v)}
            style={{ height: 30, padding: '0 10px', borderRadius: 7, border: `1px solid ${layerColor}66`, background: `${layerColor}15`, color: layerColor, fontSize: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', letterSpacing: '0.06em' }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: layerColor }} />
            {activeLayer}
            <ChevronDown className="h-2.5 w-2.5" />
          </button>
          {showLayers && (
            <div style={{ position: 'absolute', top: 34, left: 0, background: 'rgba(6,10,20,0.98)', border: '1px solid rgba(37,181,168,0.2)', borderRadius: 8, padding: 4, zIndex: 10002, minWidth: 150 }}>
              {[...BUILT_IN_LAYERS, 'Default'].map((l) => (
                <button key={l} onClick={() => { store.setActiveLayer(l); setShowLayers(false); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 7, padding: '5px 8px', borderRadius: 5, background: activeLayer === l ? 'rgba(94,234,212,0.08)' : 'transparent', border: 'none', color: LAYER_COLORS[l] ?? '#e2ecfa', fontSize: 11, cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: LAYER_COLORS[l] ?? '#5eead4', flexShrink: 0 }} />
                  {l}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.08)' }} />

        {/* Tools */}
        {TOOLS.map((t) => {
          const Icon = t.icon;
          const active = activeTool === t.id;
          return (
            <button key={t.id} title={`${t.label} (${t.shortcut})`} onClick={() => setActiveTool(t.id)}
              style={{
                height: 30, padding: '0 10px', borderRadius: 7, fontSize: 11,
                border: active ? `1px solid ${layerColor}` : '1px solid rgba(71,85,105,0.4)',
                background: active ? `${layerColor}1a` : 'transparent',
                color: active ? layerColor : 'rgba(148,163,184,0.85)',
                display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
              }}>
              <Icon className="h-3 w-3" />{t.label}
            </button>
          );
        })}

        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.08)' }} />

        {/* Grid snap toggle */}
        <button onClick={() => setGridSnap((v) => !v)} title="Grid Snap"
          style={{ height: 28, width: 28, borderRadius: 6, border: gridSnap ? `1px solid ${layerColor}66` : '1px solid rgba(71,85,105,0.4)', background: gridSnap ? `${layerColor}15` : 'transparent', color: gridSnap ? layerColor : 'rgba(148,163,184,0.7)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
          <Target className="h-3 w-3" />
        </button>

        {/* Undo / Redo */}
        <button onClick={undo} title="Undo (Ctrl+Z)"
          style={{ height: 28, width: 28, borderRadius: 6, border: '1px solid rgba(71,85,105,0.4)', background: 'transparent', color: 'rgba(148,163,184,0.8)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
          <Undo2 className="h-3 w-3" />
        </button>
        <button onClick={redo} title="Redo (Ctrl+Y)"
          style={{ height: 28, width: 28, borderRadius: 6, border: '1px solid rgba(71,85,105,0.4)', background: 'transparent', color: 'rgba(148,163,184,0.8)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
          <Redo2 className="h-3 w-3" />
        </button>

        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.08)' }} />

        {/* Close */}
        <button onClick={() => setOverlayActive(false)} title="Exit Measure Mode (ESC)"
          style={{ height: 28, width: 28, borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: 'rgba(248,113,113,0.85)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
          <X className="h-3 w-3" />
        </button>
      </div>

      {/* ── Scale indicator ───────────────────────────────────────────────── */}
      <div style={{
        position: 'fixed', bottom: 52, right: 16, zIndex: 10001,
        background: 'rgba(6,10,20,0.92)', border: `1px solid ${cal ? 'rgba(94,234,212,0.4)' : 'rgba(245,158,11,0.4)'}`,
        borderRadius: 8, padding: '5px 10px', fontSize: 10,
        color: cal ? '#5eead4' : '#fbbf24',
      }}>
        {cal ? `Scale: ${cal.label} = ${cal.realDist} ${cal.unit}` : '⚠ Scale not set — measuring in px'}
      </div>

      {/* ── Live value tooltip ────────────────────────────────────────────── */}
      {liveValue && (
        <div style={{
          position: 'fixed', left: cursor.x + 18, top: cursor.y - 14,
          zIndex: 10002, pointerEvents: 'none',
          background: 'rgba(6,10,20,0.95)', border: `1px solid ${layerColor}66`,
          borderRadius: 6, padding: '3px 9px', fontSize: 12, fontWeight: 700,
          color: layerColor, letterSpacing: '0.04em',
          boxShadow: `0 2px 10px ${layerColor}33`,
        }}>
          {liveValue}
          {pendingPoints.length > 0 && (
            <div style={{ fontSize: 9, color: 'rgba(148,163,184,0.5)', marginTop: 1 }}>
              {activeTool === 'distance' ? 'Click to confirm' : 'Dbl-click to finish · Right-click to undo'}
            </div>
          )}
        </div>
      )}

      {/* ── Status bar overlay (instruction) ─────────────────────────────── */}
      {!activeTool && (
        <div style={{
          position: 'fixed', bottom: 52, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10001, background: 'rgba(6,10,20,0.92)', border: '1px solid rgba(94,234,212,0.2)',
          borderRadius: 8, padding: '5px 14px', fontSize: 10, color: 'rgba(148,163,184,0.7)',
        }}>
          Select a tool above · D=Distance P=Polyline A=Area N=Angle R=Radius C=Calibrate · ESC to exit
        </div>
      )}

      {/* ── Calibration dialog ───────────────────────────────────────────── */}
      {showCalDialog && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)',
        }}>
          <div style={{
            background: 'rgba(6,10,20,0.98)', border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: 14, padding: 24, width: 360,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fbbf24', marginBottom: 4 }}>Set Scale</div>
            <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)', marginBottom: 16 }}>
              You clicked 2 points ({pointDist(calPts[0]!, calPts[1]!).toFixed(0)} px apart). Enter the real distance between them.
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                autoFocus type="number" placeholder="e.g. 5000"
                value={calDist} onChange={(e) => setCalDist(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyCalibration()}
                style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(71,85,105,0.5)', borderRadius: 7, padding: '8px 10px', color: '#e2ecfa', fontSize: 13 }}
              />
              <select value={calUnit} onChange={(e) => setCalUnit(e.target.value as any)}
                style={{ background: 'rgba(6,10,20,0.9)', border: '1px solid rgba(71,85,105,0.5)', borderRadius: 7, padding: '8px 10px', color: '#e2ecfa', fontSize: 13 }}>
                <option value="mm">mm</option>
                <option value="m">m</option>
                <option value="ft">ft</option>
                <option value="in">in</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={applyCalibration}
                style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1px solid rgba(245,158,11,0.5)', background: 'rgba(245,158,11,0.1)', color: '#fbbf24', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Calibrate
              </button>
              <button onClick={() => { setShowCalDialog(false); setCalPts([]); setCalDist(''); }}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(71,85,105,0.5)', background: 'transparent', color: 'rgba(148,163,184,0.7)', fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}

// ── Crosshair canvas (separate from hit area for perf) ─────────────────────────
function CrosshairCanvas({ cursor, color, snapType }: { cursor: MeasurementPoint; color: string; snapType: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const { x, y } = cursor;

    // Full-screen crosshair lines
    ctx.strokeStyle = `${color}55`;
    ctx.lineWidth = 0.75;
    ctx.setLineDash([4, 6]);
    ctx.beginPath();
    ctx.moveTo(0, y); ctx.lineTo(canvas.width, y);
    ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height);
    ctx.stroke();
    ctx.setLineDash([]);

    // Central crosshair
    const size = 12;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x - size, y); ctx.lineTo(x + size, y);
    ctx.moveTo(x, y - size); ctx.lineTo(x, y + size);
    ctx.stroke();

    // Outer circle
    ctx.beginPath();
    ctx.arc(x, y, 8, 0, Math.PI * 2);
    ctx.strokeStyle = `${color}aa`;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Snap indicator
    if (snapType !== 'free') {
      ctx.beginPath();
      if (snapType === 'endpoint') {
        ctx.rect(x - 5, y - 5, 10, 10);
        ctx.strokeStyle = '#fbbf24';
      } else if (snapType === 'midpoint') {
        ctx.moveTo(x, y - 6); ctx.lineTo(x + 6, y + 5); ctx.lineTo(x - 6, y + 5); ctx.closePath();
        ctx.strokeStyle = '#60a5fa';
      } else if (snapType === 'grid') {
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.strokeStyle = '#4ade80';
      }
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }, [cursor, color, snapType]);

  useEffect(() => {
    const onResize = () => {
      const canvas = canvasRef.current;
      if (canvas) { canvas.width = window.innerWidth; canvas.height = window.innerHeight; }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10000 }} />
  );
}

// ── Single measurement SVG renderer ──────────────────────────────────────────
function MeasurementSVG({ m, hovered, onHover, onDelete }: {
  m: Measurement; hovered: boolean;
  onHover: (id: string | null) => void;
  onDelete: () => void;
}) {
  const color = m.style?.color ?? LAYER_COLORS[m.layer] ?? '#5eead4';
  const pts = m.points;
  if (pts.length < 2) return null;

  const midX = pts.reduce((s, p) => s + p.x, 0) / pts.length;
  const midY = pts.reduce((s, p) => s + p.y, 0) / pts.length;

  const arrowMarkerId = `arrow-${m.layer}`;

  return (
    <g
      style={{ cursor: 'pointer' }}
      onMouseEnter={() => onHover(m.id)}
      onMouseLeave={() => onHover(null)}
      opacity={m.style?.opacity ?? 0.92}
    >
      {/* Lines */}
      {m.type === 'distance' && (
        <>
          <line x1={pts[0]?.x??0} y1={pts[0]?.y??0} x2={pts[1]?.x??0} y2={pts[1]?.y??0}
            stroke={color} strokeWidth={hovered ? 2.5 : 1.5}
            markerStart={`url(#${arrowMarkerId})`} markerEnd={`url(#${arrowMarkerId})`} />
          {/* Extension lines */}
          <line x1={pts[0]?.x??0} y1={(pts[0]?.y??0) - 8} x2={pts[0]?.x??0} y2={(pts[0]?.y??0) + 8} stroke={color} strokeWidth={1} opacity={0.5} />
          <line x1={pts[1]?.x??0} y1={(pts[1]?.y??0) - 8} x2={pts[1]?.x??0} y2={(pts[1]?.y??0) + 8} stroke={color} strokeWidth={1} opacity={0.5} />
        </>
      )}
      {m.type === 'polyline' && pts.length >= 2 && (
        <polyline points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none" stroke={color} strokeWidth={hovered ? 2 : 1.5} />
      )}
      {m.type === 'area' && pts.length >= 3 && (
        <>
          <polygon points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
            fill={`${color}18`} stroke={color} strokeWidth={hovered ? 2 : 1.5} />
        </>
      )}
      {m.type === 'radius' && (
        <>
          <line x1={pts[0]!.x} y1={pts[0]!.y} x2={pts[1]!.x} y2={pts[1]!.y}
            stroke={color} strokeWidth={hovered ? 2 : 1.5} markerEnd={`url(#${arrowMarkerId})`} />
          <circle cx={pts[0]!.x} cy={pts[0]!.y} r={pointDist(pts[0]!, pts[1]!)}
            fill="none" stroke={`${color}40`} strokeWidth={1} strokeDasharray="4 4" />
        </>
      )}

      {/* Label background */}
      {m.style?.showBackground !== false && (
        <rect x={midX - 26} y={midY - 10} width={52} height={16} rx={4}
          fill="rgba(6,10,20,0.88)" stroke={`${color}55`} strokeWidth={0.8} />
      )}

      {/* Label text */}
      <text x={midX} y={midY + 3} textAnchor="middle" fontSize={m.style?.fontSize ?? 10}
        fill={color} fontFamily="'Courier New', monospace" fontWeight="600">
        {m.label}
      </text>

      {/* Hover tooltip */}
      {hovered && (
        <g>
          <rect x={midX - 60} y={midY - 54} width={120} height={46} rx={6}
            fill="rgba(6,10,20,0.97)" stroke={`${color}66`} strokeWidth={1} />
          <text x={midX} y={midY - 38} textAnchor="middle" fontSize={9} fill={`${color}88`}
            fontFamily="monospace">{new Date(m.timestamp).toLocaleDateString()}</text>
          <text x={midX} y={midY - 24} textAnchor="middle" fontSize={10} fill={color}
            fontFamily="monospace" fontWeight="700">{m.label}</text>
          <text x={midX} y={midY - 12} textAnchor="middle" fontSize={9} fill="rgba(148,163,184,0.7)"
            fontFamily="monospace">{m.layer} · {m.page}</text>
          {/* Delete button */}
          <g onClick={(e) => { e.stopPropagation(); onDelete(); }} style={{ cursor: 'pointer' }}>
            <rect x={midX + 55} y={midY - 54} width={16} height={14} rx={3}
              fill="rgba(239,68,68,0.15)" stroke="rgba(239,68,68,0.4)" strokeWidth={0.8} />
            <text x={midX + 63} y={midY - 44} textAnchor="middle" fontSize={8} fill="#f87171">✕</text>
          </g>
        </g>
      )}
    </g>
  );
}
